import { FC } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Icon } from '@opallabs/lapidary'
import { ScheduledTime } from '@opallabs/periodic/src/ScheduledTime'
import { AccountName } from '../../../../../AccountName'
import { ContentAvatar } from '../../../../../ContentAvatar'
import { ContentTextInput } from '../../../../../ContentTextInput'
import type { Asset } from '../../../../helpers/types'
import { DocumentCarousel } from '../../components/DocumentCarousel'
import { LinkedInDocumentDisplayProps } from '../../types'
import * as styles from './styles'

const PostHeader: FC<{
  accountImage: Asset | null
  accountName: string
  scheduledAt?: string
  timezone?: string
}> = ({ accountImage, accountName, scheduledAt, timezone }) => (
  <div css={styles.header}>
    <ContentAvatar
      assetReference={accountImage}
      size={40}
      style={styles.avatar}
    />
    <div css={styles.headerInfo}>
      <AccountName
        name={accountName}
        noAccountColor="#fab14d"
        size={14}
        style={styles.accountNameStyle}
      />
      <span css={styles.headerMeta}>
        <ScheduledTime
          color="rgba(0, 0, 0, 0.6)"
          dateFormat="MMM d, yyyy"
          fontSize={12}
          timestamp={scheduledAt}
          timezone={timezone}
        />
      </span>
    </div>
  </div>
)

const PostContent: FC<{
  content: string
  onChange: (value: string) => void
  readOnly: boolean
}> = ({ content, onChange, readOnly }) => (
  <div css={styles.contentWrapper}>
    <ContentTextInput
      ariaLabel="Post content"
      characterLimit={3000}
      data-readonly={readOnly}
      minRows={1}
      maxRows={6}
      placeholder="What do you want to talk about?"
      readOnly={readOnly}
      style={styles.contentTextInputStyle}
      value={content}
      onChange={onChange}
    />
  </div>
)

const PostActions = () => (
  <div css={styles.actions}>
    <div css={styles.iconActions}>
      <div css={styles.actionIcon}>
        <Icon
          glyph="linkedin-mobile-like"
          useHypernova
          width={20}
          height={20}
        />
      </div>
      <div css={styles.actionIcon}>
        <Icon
          glyph="linkedin-mobile-comment"
          useHypernova
          width={20}
          height={20}
        />
      </div>
    </div>
    <div css={styles.commentInput}>
      <div css={styles.actionIcon}>
        <Icon
          glyph="linkedin-mobile-share"
          useHypernova
          width={20}
          height={20}
        />
      </div>
      <span css={styles.commentPlaceholder}>Comment...</span>
    </div>
  </div>
)

export const MobileNewsFeed: FC<LinkedInDocumentDisplayProps> = ({
  accountImage,
  accountName,
  assets,
  content,
  documentTitle,
  isPresenting,
  onContentChange,
  onDocumentTitleChange,
  onExpandAsset,
  onRemoveAsset,
  readOnly,
  scheduledAt,
  timezone,
}) => {
  const pdfAsset: AssetReference | null = assets?.[0] ?? null

  return (
    <div css={styles.container}>
      <PostHeader
        accountImage={accountImage}
        accountName={accountName}
        scheduledAt={scheduledAt}
        timezone={timezone}
      />

      <PostContent
        content={content}
        onChange={onContentChange}
        readOnly={readOnly || isPresenting}
      />

      <DocumentCarousel
        assetReference={pdfAsset}
        documentTitle={documentTitle}
        isPresenting={isPresenting}
        onDocumentTitleChange={onDocumentTitleChange}
        onExpandAsset={onExpandAsset}
        onRemoveAsset={onRemoveAsset}
        readOnly={readOnly}
        width={styles.carouselWidth}
      />

      <PostActions />
    </div>
  )
}
